"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = exports.VideoProps = void 0;
var _componentsBase = require("@telus-uds/components-base");
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _reactYoutube = _interopRequireDefault(require("react-youtube"));
var _styledComponents = _interopRequireDefault(require("styled-components"));
var _VideoSplash = _interopRequireDefault(require("../shared/VideoSplash/VideoSplash"));
var _utils = require("../utils");
var _utils2 = require("./utils");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const [selectProps, selectedSystemPropTypes] = (0, _componentsBase.selectSystemProps)([_utils.htmlAttrs]);
const StyledPlayerContainer = /*#__PURE__*/_styledComponents.default.div.withConfig({
  displayName: "WebVideo__StyledPlayerContainer",
  componentId: "components-web__sc-327a6k-0"
})({
  width: '100%',
  minWidth: 288,
  outline: 'none'
});
const StyledYoutubePlayer = /*#__PURE__*/(0, _styledComponents.default)(_reactYoutube.default).withConfig({
  displayName: "WebVideo__StyledYoutubePlayer",
  componentId: "components-web__sc-327a6k-1"
})({
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  right: 0
});
const aspectRatios = {
  '16:9': {
    paddingTop: '56.25%'
  },
  '4:3': {
    paddingTop: '75%'
  },
  '1:1': {
    paddingTop: '100%'
  }
};
const AspectLimiter = /*#__PURE__*/_styledComponents.default.div.withConfig({
  displayName: "WebVideo__AspectLimiter",
  componentId: "components-web__sc-327a6k-2"
})(props => ({
  ...aspectRatios[props.aspectRatio],
  position: 'relative'
}));
const WebVideo = /*#__PURE__*/_react.default.forwardRef((_ref, ref) => {
  let {
    videoId,
    aspectRatio = '16:9',
    posterSrc,
    defaultVolume = 1,
    beginMuted = false,
    videoLength,
    copy,
    onPlay = () => {},
    onPause = () => {},
    onEnd = () => {},
    onProgress = () => {},
    onStart = () => {},
    ...rest
  } = _ref;
  const [started, setStarted] = _react.default.useState(false);
  const videoStateData = _react.default.useRef({
    requestAnimationIds: [],
    state: _utils2.YoutubePlayerState.UNSTARTED
  });
  const playerRef = _react.default.useRef(null);
  const onPlayCallback = event => {
    onPlay(event, videoStateData.current.state === _utils2.YoutubePlayerState.PAUSED);
    videoStateData.current.state = _utils2.YoutubePlayerState.PLAYING;
    if (onProgress) {
      videoStateData.current.requestAnimationIds = (0, _utils2.triggerInProgressVideoIntervals)(onProgress, playerRef, event);
    }
  };
  const initializeYoutubePlayer = event => {
    onStart();
    playerRef.current = event.target;
    if (beginMuted) {
      event.target.mute();
    }
    event.target.setVolume(defaultVolume);
    event.target.playVideo(); // This plays the video after passing the splash screen on mobile.
  };
  const onEndVideoCallback = event => {
    onEnd(event);
    videoStateData.current.requestAnimationIds.forEach(id => cancelAnimationFrame(id));
    videoStateData.current.requestAnimationIds = [];
    videoStateData.current.state = _utils2.YoutubePlayerState.ENDED;
    if (onProgress) {
      onProgress(event, 100);
    }
  };
  const onPauseVideoCallback = event => {
    videoStateData.current.requestAnimationIds.forEach(id => cancelAnimationFrame(id));
    onPause(event);
    videoStateData.current.state = _utils2.YoutubePlayerState.PAUSED;
    videoStateData.current.requestAnimationIds = [];
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(StyledPlayerContainer, {
    ref: ref,
    "data-testid": "web-video-container",
    ...selectProps(rest),
    children: /*#__PURE__*/(0, _jsxRuntime.jsx)(AspectLimiter, {
      aspectRatio: aspectRatio,
      children: started ? /*#__PURE__*/(0, _jsxRuntime.jsx)(StyledYoutubePlayer, {
        videoId: videoId,
        opts: {
          width: '100%',
          height: '100%',
          playerVars: {
            autoplay: 1,
            modestbranding: 1,
            playsinline: 1,
            rel: 0
          }
        },
        onReady: initializeYoutubePlayer,
        onPlay: onPlayCallback,
        onPause: onPauseVideoCallback,
        onEnd: onEndVideoCallback
      }) : /*#__PURE__*/(0, _jsxRuntime.jsx)(_VideoSplash.default, {
        poster: posterSrc || `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
        videoLength: videoLength,
        copy: copy,
        onClick: () => {
          setStarted(true);
        }
      })
    })
  });
});
WebVideo.displayName = 'WebVideo';
const VideoProps = exports.VideoProps = {
  ...selectedSystemPropTypes,
  /**
   * The video's ID, typically available via the video's URL.
   */
  videoId: _propTypes.default.string.isRequired,
  /**
   * The aspect ratio of the player.
   */
  aspectRatio: _propTypes.default.oneOf(['16:9', '4:3', '1:1']),
  /**
   * A path of the image that will be displayed on the video's splash screen. If this is undefined, it will pull an image from the defined web video if available.
   */
  posterSrc: _propTypes.default.string,
  /**
   * The video's default volume, defined from 1 to 100. Please use the `beginMuted` prop to have the video start silenced.
   */
  defaultVolume: _propTypes.default.number,
  /**
   * Defines if the video should start muted.
   */
  beginMuted: _propTypes.default.bool,
  /**
   * The video's length, which will be displayed on the splash screen. This is defined in seconds.
   */
  videoLength: _propTypes.default.number.isRequired,
  /**
   * The splash screen UI's language as an ISO language code. It currently supports English and French.
   */
  copy: _propTypes.default.oneOf(['en', 'fr']),
  /**
   * A function to be run when the play button is pressed on the video splash screen and the video is ready to play.
   */
  onStart: _propTypes.default.func,
  /**
   * A function to be run when the video is played.
   */
  onPlay: _propTypes.default.func,
  /**
   * A function to be run when the video is paused.
   */
  onPause: _propTypes.default.func,
  /**
   * A function to be run when the video ends.
   */
  onEnd: _propTypes.default.func,
  /**
   * A function to be run when the video resumes.
   */
  onResume: _propTypes.default.func,
  /**
   * A function to be run when the video progresses. This function will be run at 10%, 25%, 50%, 75% and 100%.
   */
  onProgress: _propTypes.default.func
};
WebVideo.propTypes = VideoProps;
var _default = exports.default = WebVideo;